import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useScoreCardView,
  useUserScoreCards,
} from "../../../@apis/hooks/useTournament";
import type { ScoreCard } from "../../../@apis/dto/response/score.response";
import { toScoreSections } from "../../../lib/utils/score.utils";
import { Legend, ScoreCardHeader, ScoreTables } from "../../atoms/tables";

const LOADING_TEXT = (
  <div className="py-[20px] text-center text-[#999] text-[14px]">
    불러오는 중...
  </div>
);
const EMPTY_TEXT = (
  <div className="py-[20px] text-center text-[#999] text-[14px]">
    스코어 정보가 없습니다.
  </div>
);

// 멀티라운드 라운드명 태그
function RoundTag({ name }: { name: string }) {
  return (
    <div className="mb-[12px] font-bold text-lg p-[8px] w-fit rounded-md bg-black text-white">
      {name}
    </div>
  );
}

// 비참가(실격/기권/불참) 라운드: 라운드명 태그 + 스코어 없음 안내
function EmptyRoundCard({ roundName }: { roundName?: string }) {
  return (
    <div>
      {roundName && <RoundTag name={roundName} />}
      {EMPTY_TEXT}
    </div>
  );
}

// 스코어 카드 1장 본문 (이름/점수 라인 + 테이블)
function ScoreCardBody({
  card,
  showRoundName,
}: {
  card: ScoreCard;
  showRoundName?: boolean;
}) {
  return (
    <div>
      {/* 멀티라운드: 라운드명 라인 */}
      {showRoundName && card.roundName && (
        <RoundTag name={card.roundName} />
      )}

      <ScoreCardHeader card={card} />

      <ScoreTables sections={toScoreSections(card)} />
      <Legend />
    </div>
  );
}

// 일정대회: 확장된 행에 표시할 스코어 카드 (단건조회)
export function ScoreCardViewPanel({
  tournamentId,
  calendarId,
  scoreCardId,
}: {
  tournamentId: number | null | undefined;
  calendarId: number | null;
  scoreCardId: number | null;
}) {
  const { data, isLoading } = useQuery({
    ...useScoreCardView(tournamentId ?? 0, calendarId, scoreCardId),
  });

  const card = data?.data?.scoreCard;

  return (
    <div className="px-[10px] py-[15px] bg-[#EAF6FF] border-t border-[#eee]">
      {isLoading ? (
        LOADING_TEXT
      ) : !card ? (
        EMPTY_TEXT
      ) : (
        <ScoreCardBody card={card} />
      )}
    </div>
  );
}

// 멀티라운드: 확장된 행에 사용자의 라운드 스코어카드 표시
// (선택한 라운드까지 누적 표시: 참가 라운드=카드, 비참가 라운드=빈 카드)
export function UserScoreCardsPanel({
  tournamentId,
  calendarId,
  userId,
  nonPartRoundId,
  nonPartRoundName,
  maxRoundOrder,
  roundOrderById,
}: {
  tournamentId: number | null | undefined;
  calendarId: number | null;
  userId: number | null;
  nonPartRoundId: number | null; // 비참가(실격/기권/불참) 처리된 라운드
  nonPartRoundName?: string; // 비참가 라운드명 (카드가 없을 때 태그용)
  maxRoundOrder: number | null; // 선택 라운드 순서 (전체면 null, 이 순서까지만 노출)
  roundOrderById: Map<number, number>; // roundId → roundOrder
}) {
  const { data, isLoading } = useQuery({
    ...useUserScoreCards(tournamentId ?? 0, calendarId, userId),
  });

  const allCards = data?.data?.scoreCardList ?? [];
  const orderOf = (rid: number) => roundOrderById.get(rid) ?? 0;
  const withinRange = (rid: number) =>
    maxRoundOrder == null || orderOf(rid) <= maxRoundOrder;

  // 참가 라운드 카드 (비참가 라운드는 빈 카드로 따로 표시), 선택 라운드까지만
  const cards = allCards.filter(
    (c): c is ScoreCard & { roundId: number } =>
      c.roundId != null &&
      c.roundId !== nonPartRoundId &&
      withinRange(c.roundId),
  );

  const showNonPart = nonPartRoundId != null && withinRange(nonPartRoundId);
  const nonPartName =
    allCards.find((c) => c.roundId === nonPartRoundId)?.roundName ??
    nonPartRoundName;

  // 라운드 순서대로 정렬해 표시
  const items: { key: string; roundId: number; node: React.ReactNode }[] =
    cards.map((c) => ({
      key: `c-${c.scoreCardId}`,
      roundId: c.roundId,
      node: <ScoreCardBody card={c} showRoundName />,
    }));
  if (showNonPart) {
    items.push({
      key: "np",
      roundId: nonPartRoundId,
      node: <EmptyRoundCard roundName={nonPartName} />,
    });
  }
  items.sort((a, b) => orderOf(a.roundId) - orderOf(b.roundId));

  return (
    <div className="px-[10px] py-[15px] bg-[#EAF6FF] border-t border-[#eee] space-y-[30px]">
      {isLoading
        ? LOADING_TEXT
        : items.length === 0
          ? EMPTY_TEXT
          : items.map((it) => <div key={it.key}>{it.node}</div>)}
    </div>
  );
}
